import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { optimizeStopOrder } from "@/lib/routing";

const logActivity = async (tripId: string, userId: string, action: string, details?: string) => {
  const { error } = await supabase.from("trip_activity").insert({
    trip_id: tripId,
    user_id: userId,
    action,
    details: details || null,
  } as any);
  if (error) {
    console.error("[logActivity] Failed to log trip activity:", error.message, { tripId, action });
  }
};

export const useTrips = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: tripsData = { own: [], shared: [] }, isLoading } = useQuery({
    queryKey: ["trips", user?.id],
    queryFn: async () => {
      if (!user) return { own: [], shared: [] };

      // Fetch own trips
      const { data: ownTrips, error: ownErr } = await supabase
        .from("trips")
        .select("*, trip_stops(*, campings(*)), trip_collaborators(id, user_id, role)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (ownErr) throw ownErr;

      // Fetch collaborative trips
      const { data: collabs } = await supabase
        .from("trip_collaborators")
        .select("trip_id, invited_by")
        .eq("user_id", user.id) as any;

      const collabTripIds = (collabs || []).map((c: any) => c.trip_id).filter(
        (id: string) => !(ownTrips || []).some((t: any) => t.id === id)
      );

      let sharedTrips: any[] = [];
      if (collabTripIds.length > 0) {
        const { data } = await supabase
          .from("trips")
          .select("*, trip_stops(*, campings(*)), trip_collaborators(id, user_id, role)")
          .in("id", collabTripIds)
          .order("created_at", { ascending: false });
        // Attach inviter info
        sharedTrips = (data || []).map((t: any) => {
          const collab = (collabs || []).find((c: any) => c.trip_id === t.id);
          return { ...t, _invitedBy: collab?.profiles?.display_name || null };
        });
      }

      return { own: ownTrips || [], shared: sharedTrips };
    },
    enabled: !!user,
  });

  // Realtime subscriptions for collaborative updates (all trip-related tables)
  useEffect(() => {
    if (!user) return;

    const relevantTripIds = new Set([
      ...tripsData.own.map((trip: any) => trip.id),
      ...tripsData.shared.map((trip: any) => trip.id),
    ]);

    const isRelevantTrip = (tripId?: string | null) => !!tripId && relevantTripIds.has(tripId);
    const invalidateTrips = () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    };

    const channel = supabase
      .channel(`trips-realtime-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "trips" }, (payload) => {
        const tripId = (payload.new as any)?.id ?? (payload.old as any)?.id;
        const ownerId = (payload.new as any)?.user_id ?? (payload.old as any)?.user_id;

        if (ownerId === user.id || isRelevantTrip(tripId)) {
          invalidateTrips();
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "trip_collaborators" }, (payload) => {
        const tripId = (payload.new as any)?.trip_id ?? (payload.old as any)?.trip_id;
        const collaboratorUserId = (payload.new as any)?.user_id ?? (payload.old as any)?.user_id;

        if (collaboratorUserId === user.id || isRelevantTrip(tripId)) {
          invalidateTrips();
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "trip_stops" }, (payload) => {
        const tripId = (payload.new as any)?.trip_id ?? (payload.old as any)?.trip_id;
        if (isRelevantTrip(tripId)) {
          invalidateTrips();
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "trip_expenses" }, (payload) => {
        const tripId = (payload.new as any)?.trip_id ?? (payload.old as any)?.trip_id;
        if (isRelevantTrip(tripId)) {
          queryClient.invalidateQueries({ queryKey: ["trip-expenses"] });
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "checklist_items" }, (payload) => {
        const tripId = (payload.new as any)?.trip_id ?? (payload.old as any)?.trip_id;
        if (isRelevantTrip(tripId)) {
          queryClient.invalidateQueries({ queryKey: ["checklist"] });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient, tripsData.own, tripsData.shared]);

  const trips = [...tripsData.own, ...tripsData.shared];
  const ownTrips = tripsData.own;
  const sharedTrips = tripsData.shared;

  const createTrip = useMutation({
    mutationFn: async (trip: {
      title: string;
      destination?: string;
      start_date?: string;
      end_date?: string;
      fuel_consumption?: number;
      fuel_price?: number;
      currency?: string;
      budget?: number;
    }) => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("trips")
        .insert({ ...trip, user_id: user.id } as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["trips"] }),
  });

  const updateTrip = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { error } = await supabase
        .from("trips")
        .update(updates as any)
        .eq("id", id);
      if (error) throw error;
      if (user) logActivity(id, user.id, "updated_trip");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["trips"] }),
  });

  const deleteTrip = useMutation({
    mutationFn: async (tripId: string) => {
      const { error } = await supabase.from("trips").delete().eq("id", tripId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["trips"] }),
  });

  const addStop = useMutation({
    mutationFn: async (stop: {
      trip_id: string;
      camping_id?: string | null;
      notes?: string;
      stop_order?: number;
      stop_name?: string;
      stop_address?: string;
      latitude?: number;
      longitude?: number;
      stop_type?: string;
      stay_duration_hours?: number | null;
      nights?: number;
      leg?: string;
      accommodation_name?: string | null;
      accommodation_latitude?: number | null;
      accommodation_longitude?: number | null;
      accommodation_place_id?: string | null;
      accommodation_distance_km?: number | null;
      accommodation_rating?: number | null;
    }) => {
      // If stop_order is provided AND collides with existing stops, push the rest +1
      if (typeof stop.stop_order === "number") {
        const { data: existing, error: fetchErr } = await supabase
          .from("trip_stops")
          .select("id, stop_order")
          .eq("trip_id", stop.trip_id)
          .gte("stop_order", stop.stop_order)
          .order("stop_order", { ascending: false }); // descending so we update last-first
        if (fetchErr) throw fetchErr;

        // Shift each conflicting stop's order up by 1, starting from the highest
        for (const row of existing || []) {
          const { error: updErr } = await supabase
            .from("trip_stops")
            .update({ stop_order: (row.stop_order ?? 0) + 1 })
            .eq("id", row.id);
          if (updErr) throw updErr;
        }
      }

      const { error } = await supabase.from("trip_stops").insert(stop as any);
      if (error) throw error;
      if (user) logActivity(stop.trip_id, user.id, "added_stop", stop.stop_name || stop.stop_address);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["trips"] }),
    onError: (err: any) => {
      console.error("[addStop] failed:", err);
    },
  });

  const updateStop = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { error } = await supabase
        .from("trip_stops")
        .update(updates as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["trips"] }),
  });

  const removeStop = useMutation({
    mutationFn: async (stopId: string) => {
      const { error } = await supabase.from("trip_stops").delete().eq("id", stopId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["trips"] }),
  });

  // Remove stop and re-optimize the remaining stop order using nearest-neighbor
  const removeAndOptimize = useMutation({
    mutationFn: async ({ stopId, tripId }: { stopId: string; tripId: string }) => {
      // Delete the stop
      const { error: delErr } = await supabase.from("trip_stops").delete().eq("id", stopId);
      if (delErr) throw delErr;

      // Fetch remaining stops
      const { data: remaining, error: fetchErr } = await supabase
        .from("trip_stops")
        .select("id, stop_order, latitude, longitude, stop_name")
        .eq("trip_id", tripId)
        .order("stop_order", { ascending: true });
      if (fetchErr) throw fetchErr;
      if (!remaining || remaining.length < 2) return;

      // Find start (order 0) and end (last)
      const start = remaining[0];
      const end = remaining[remaining.length - 1];
      const middle = remaining.slice(1, -1);

      if (middle.length < 2) {
        // Just reorder sequentially
        for (let i = 0; i < remaining.length; i++) {
          if (remaining[i].stop_order !== i) {
            await supabase.from("trip_stops").update({ stop_order: i }).eq("id", remaining[i].id);
          }
        }
        return;
      }

      // Geographic optimization for middle stops using projection-based sorting
      const optimized = optimizeStopOrder(
        start.latitude || 0, start.longitude || 0,
        end.latitude || 0, end.longitude || 0,
        middle,
      );

      // Update stop_order: start=0, optimized middle, end=last
      const reordered = [start, ...optimized, end];
      for (let i = 0; i < reordered.length; i++) {
        if (reordered[i].stop_order !== i) {
          await supabase.from("trip_stops").update({ stop_order: i }).eq("id", reordered[i].id);
        }
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["trips"] }),
  });

  const reoptimizeStops = useMutation({
    mutationFn: async (tripId: string) => {
      const { data: stops, error } = await supabase
        .from("trip_stops")
        .select("id, stop_order, latitude, longitude, stop_name")
        .eq("trip_id", tripId)
        .order("stop_order", { ascending: true });
      if (error) throw error;
      if (!stops || stops.length < 3) return;

      const start = stops[0];
      const end = stops[stops.length - 1];
      const middle = stops.slice(1, -1);

      const optimized = optimizeStopOrder(
        start.latitude || 0, start.longitude || 0,
        end.latitude || 0, end.longitude || 0,
        middle,
      );

      const reordered = [start, ...optimized, end];
      for (let i = 0; i < reordered.length; i++) {
        if (reordered[i].stop_order !== i) {
          await supabase.from("trip_stops").update({ stop_order: i }).eq("id", reordered[i].id);
        }
      }
      if (user) logActivity(tripId, user.id, "reoptimized_route");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["trips"] }),
  });

  return { trips, ownTrips, sharedTrips, isLoading, createTrip, updateTrip, deleteTrip, addStop, updateStop, removeStop, removeAndOptimize, reoptimizeStops };
};
